import React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import { UserMenu } from './UserMenu';
import { useScrollTrigger } from '@mui/material';
import { ContextActions } from '@/components/contextActions/ContextActions';
import { NavToggle } from './NavToggle';
import { ActiveHospitalSelect } from './ActiveHospitalSelect';
import { LayoutGroup, motion } from 'framer-motion';

interface StyledAppBarProps extends MuiAppBarProps {
  open: boolean;
  drawerWidth: number;
}

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'open' && prop !== 'drawerWidth' && prop !== 'scrolled'
})<StyledAppBarProps & { scrolled: boolean }>(
  ({ theme, open, drawerWidth, scrolled }) => ({
    overflow: 'hidden',
    // A hairline plus a soft shadow, both drawn as shadows so the bar keeps its height
    boxShadow: scrolled
      ? `0 1px 0 ${theme.palette.themeColors.slate[200]}, 0 4px 12px -6px rgba(15, 23, 42, 0.12)`
      : 'none',
    transition: theme.transitions.create(['margin', 'width', 'box-shadow'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    ...(open && {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: `${drawerWidth}px`,
      transition: theme.transitions.create(['margin', 'width', 'box-shadow'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen
      })
    })
  })
);

interface AppBarProps extends StyledAppBarProps {
  onToggleClick: () => void;
}

/** Top bar fixed over every authenticated page; the page scrolls under it. */
export function AppBar({ onToggleClick, ...props }: AppBarProps) {
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 0 });

  return (
    <StyledAppBar
      elevation={0}
      position="fixed"
      color="transparent"
      scrolled={scrolled}
      {...props}
    >
      <Toolbar>
        <Box mr={2}>
          <NavToggle onClick={onToggleClick} />
        </Box>
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <LayoutGroup>
            <Box layout component={motion.div} flexGrow={1}>
              <ContextActions />
            </Box>
            <motion.div layout>
              <ActiveHospitalSelect />
            </motion.div>
            <motion.div layout>
              <UserMenu />
            </motion.div>
          </LayoutGroup>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
}
